import { useContext, useEffect, useMemo, useState } from "react";

import { getExtensionValue } from "@medplum/core";
import { Coding, QuestionnaireItem } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import {
  Box,
  ComboboxItem,
  Divider,
  Flex,
  Group,
  MultiSelect,
  UnstyledButton,
} from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { IconTrash } from "@tabler/icons-react";
import omit from "lodash/omit";

import { TextWrapper } from "@/components/TextWrapper";
import { QuestionnaireContext } from "@/contexts/questionnaire";
import { Extensions } from "@/fhir/enums/extensions";
import {
  getFormsetParentPath,
  getUpdatedFormsetValues,
  getUpdatedSelectData,
  isEmptyAnswerValue,
  parseAnswerCoding,
} from "@/intake-forms/utils/multi-select";
import { sortEmptyAnswerOptionsFirst } from "@/intake-forms/utils/value-set-sorting";
import { findQuestionnaireItem, getValueSetOptions } from "@/utils/medplum";
import { GenericFormValues } from "@/utils/questionnaires";

import { questionnaireFieldTestId } from "../../utils/questionnaire-field-testid";
import QuestionnaireForm from "../QuestionnaireForm";

interface QuestionnaireMultiSelectFieldProps<T> {
  group: QuestionnaireItem | undefined;
  label: string;
  name: string;
  form: UseFormReturnType<T>;
  formsetLinkId?: string;
}

interface FormsetFieldProps<T> {
  group: QuestionnaireItem;
  form: UseFormReturnType<T>;
  valueString: string;
  onDelete: (valueString: string, valueCoding: Coding) => void;
  name: string;
}

export default function QuestionnaireMultiSelectField<T>({
  group,
  label,
  name,
  form,
  formsetLinkId,
}: QuestionnaireMultiSelectFieldProps<T>) {
  const medplum = useMedplum();
  const [data, setData] = useState<ComboboxItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { value, onChange, ...formInputProps } = form.getInputProps(name);
  const questionnaire = useContext(QuestionnaireContext);

  const formsetItem = useMemo(() => {
    if (!formsetLinkId) return;
    return findQuestionnaireItem(questionnaire?.item, formsetLinkId);
  }, [questionnaire, formsetLinkId]);

  const valueCodeArray: string[] = useMemo(() => {
    if (isLoading) return [];

    return value?.map((item: { valueCoding: Coding } | string) => {
      if (typeof item === "string") return item;

      return JSON.stringify(item?.valueCoding);
    });
  }, [isLoading, value]);

  const answerValueSet = group?.answerValueSet;
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      const data = sortEmptyAnswerOptionsFirst(await getValueSetOptions(medplum, answerValueSet));
      if (cancelled) return;
      setData(
        getUpdatedSelectData(
          data.map((option) => ({
            label: option.display,
            value: JSON.stringify(option),
          })) as ComboboxItem[],
          valueCodeArray,
          formsetItem,
        ),
      );
      setIsLoading(false);
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [medplum, answerValueSet, valueCodeArray, formsetItem]);

  const placeholder = getExtensionValue(group, Extensions.QuestionnaireFieldPlaceholder) as
    | string
    | undefined;

  const handleChange = (values: string[]) => {
    if (!formsetLinkId || !formsetItem) {
      onChange(values);
      return;
    }

    const updatedValues = getUpdatedFormsetValues(
      formsetItem,
      form.values as GenericFormValues,
      values,
    );
    form.setValues((prev) => ({ ...prev, ...updatedValues }));
    onChange(values);
  };

  const handleFormsetDelete = (valueString: string, valueCoding: Coding) => {
    if (!formsetLinkId || !group) return;

    const selectedValues = valueCodeArray.filter((value: string) => value !== valueString);

    const nestedLevels = name.split(".").length > 1 ? name.split(".")?.slice(0, -1) : [];

    form.setValues((prev) => {
      if (nestedLevels.length === 0) {
        const prevFormset = (prev as GenericFormValues)[formsetLinkId];
        return {
          ...prev,
          [group.linkId]: selectedValues,
          [formsetLinkId]: omit(prevFormset, valueCoding.code!),
        } as Partial<T>;
      }

      const applyNestedUpdate = (
        obj: Partial<T> & Record<string, unknown>,
        levels: string[],
        currentLevel: number = 0,
      ): Partial<T> => {
        if (currentLevel === levels.length) {
          const prevFormset = (obj as GenericFormValues)[formsetLinkId];
          return {
            ...obj,
            [group.linkId]: selectedValues,
            [formsetLinkId]: omit(prevFormset, valueCoding.code!),
          } as Partial<T>;
        }

        const currentKey = levels[currentLevel];
        return {
          ...obj,
          [currentKey]: applyNestedUpdate(
            (obj[currentKey] as Partial<T> & Record<string, unknown>) || {},
            levels,
            currentLevel + 1,
          ),
        } as Partial<T>;
      };

      return applyNestedUpdate(prev as Partial<T> & Record<string, unknown>, nestedLevels);
    });
  };

  if (!data) return null;

  return (
    <>
      <MultiSelect
        name={name}
        data={data}
        label={label}
        withScrollArea
        value={valueCodeArray}
        onChange={handleChange}
        placeholder={placeholder}
        data-testid={questionnaireFieldTestId(name)}
        {...formInputProps}
      />
      {valueCodeArray?.map((valueString: string) => (
        <FormsetField<T>
          name={name}
          form={form}
          key={valueString}
          group={formsetItem!}
          valueString={valueString}
          onDelete={handleFormsetDelete}
        />
      ))}
    </>
  );
}

function FormsetField<T>({ group, form, valueString, onDelete, name }: FormsetFieldProps<T>) {
  if (isEmptyAnswerValue(valueString) || !group?.linkId) {
    return null;
  }

  const valueCoding = parseAnswerCoding(valueString);
  const parentPath = getFormsetParentPath(name, group.linkId, valueCoding);

  return (
    <Box data-testid={`formset-${valueCoding.display}`}>
      <Divider mb="lg" c="cream.4" />
      <Group mb="md" justify="space-between">
        <TextWrapper c="gray.7" variant="bold">
          {valueCoding.display}
        </TextWrapper>
        <UnstyledButton
          tabIndex={-1}
          data-testid={`delete-${valueCoding.display}`}
          onClick={() => onDelete(valueString, valueCoding)}
        >
          <TextWrapper c="red.9" variant="bold">
            <IconTrash />
          </TextWrapper>
        </UnstyledButton>
      </Group>
      <Flex gap="lg" direction="column">
        <QuestionnaireForm form={form} group={group} parentPath={parentPath} />
      </Flex>
    </Box>
  );
}
